import { Close } from "@mui/icons-material";
import { Alert, AlertTitle, Button } from "@mui/material";
import { SnackbarKey, useSnackbar } from "notistack";
import { ReactNode, useState } from "react";
import useService from "../hooks/useService";
import { BannerName } from "../misc/banner.type";
import userService from "../services/users.service";
import MainContext, { ServiceOptions } from "./mainContext";

const MainProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [banner, setBanner] = useState<BannerName>(undefined);
  const [bannerLink, setBannerLink] = useState<string>();

  const [currentUser, loadingCurrentUser, , reloadCurrentUser] = useService(userService.current);

  const notify = (title: string, message: string, type: "success" | "error" | "info" | "warning") => {
    var content = (
      <Alert severity={type} variant="filled">
        <AlertTitle>{title}</AlertTitle> {message}
      </Alert>
    );
    enqueueSnackbar(content, {
      variant: type,
      autoHideDuration: null,
      persist: true,
      anchorOrigin: { horizontal: "right", vertical: "top" },
      className: "app-notification",
      action: (snackbarId: SnackbarKey) => (
        <Button
          onClick={() => {
            closeSnackbar(snackbarId);
          }}
        >
          <Close />
        </Button>
      ),
    });
  };

  const notifyError = (error: any) => {
    let message: any = null;
    if ("response" in error) {
      // error is AxiosError
      const serverMessage = error.response ? error.response.data?.message : "";
      if (!serverMessage) {
        console.log({ message: error.message, config: error.config }); // eslint-disable-line no-console
      }
      message = serverMessage || "Unexpected Error";
    } else {
      message = error.message;
    }
    notify("Error", message, "error");
  };

  async function service<T>(func: () => Promise<T>, options?: ServiceOptions<T>): Promise<void> {
    if (!func) return;
    const loader = options && options.loader ? options.loader : () => {};
    const callback = options && options.callback ? options.callback : () => {};
    const handleError = options && options.handleError ? options.handleError : undefined;
    try {
      loader(true);
      const data = await func();
      loader(false);
      callback(data);
    } catch (error: any) {
      loader(false);
      if (handleError) handleError(error);
      else notifyError(error);
    }
  }

  return (
    <MainContext.Provider
      value={{
        currentUser,
        loadingCurrentUser,
        banner,
        setBanner,
        bannerLink,
        setBannerLink,
        reloadCurrentUser,
        notify,
        notifyError,
        service,
      }}
    >
      {children}
    </MainContext.Provider>
  );
};

export default MainProvider;
